import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { ClientsService } from '../../clients/clients.service';
import { ModalDialogueService } from '../../../../shared/components/modal-dialogue/modal-dialogue.service';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import {
  convertUTCStringToOffset,
  formatDateTimeStamp,
  getFormattedDateTime,
  isLargeScreen,
} from '../../../../../global.variable';
import { getDateTimeRange } from '../../tableDetails';
import { ToasterService } from '../../../../shared/services/toaster.service';
import { LoadingSpinnerService } from '../../../../shared/services/loading-spinner.service';
import { ActivatedRoute, Router } from '@angular/router';
import { UsersService } from '../../users/users.service';
import { ModifyLetterheadLogoComponent } from '../../../../shared/components/modify-letterhead-logo/modify-letterhead-logo.component';
import { CheckpointService } from '../../checkpoints/checkpoint.service';
import { PagesService } from '../../pages.service';
import { UserPromptService } from '../../user-prompts/user-prompt.service';
import { ConfirmDialogComponent } from '../../../../shared/components/confirm-dialog/confirm-dialog.component';
import { MediaPreviewComponent } from '../../../../shared/components/media-preview/media-preview.component';
import { JobsService } from '../../jobs/jobs.service';

@Component({
  selector: 'gtapp-patrol-report',
  standalone: false,
  templateUrl: './patrol-report.component.html',
  styleUrl: './patrol-report.component.scss',
})
export class PatrolReportComponent implements AfterViewInit, OnDestroy {
  @Output() closeTab = new EventEmitter();
  largeView: Boolean = isLargeScreen;
  @ViewChild('patrolFilterTemplate') patrolFilterTemplate: any;

  reportSiteClientLookup = {
    client: { data: null as any, disabled: false },
    site: { data: null as any, disabled: false },
  };
  clientDropDownList: any = [];
  siteDropDownList: any = [];
  userDropDownList: any = [];

  initialDataList = { client: [], site: [], user: [] };

  selectedReportUpdatesToFetch = new Set([1, 2]); // can have 1-patrol 2-job

  selectedUserData: any;

  initialTimeRange = getDateTimeRange();

  selectedDateRange = {
    start: getFormattedDateTime(this.initialTimeRange.dateRange.start),
    end: getFormattedDateTime(this.initialTimeRange.dateRange.end),
  };

  timeSelectValues: any = [
    { key: 0, value: '00:00' },
    { key: 6, value: '06:00' },
    { key: 18, value: '18:00' },
  ];
  dateSelectValues: any = [
    { key: 0, value: 'Last 12 Hours' },
    { key: 1, value: 'Last 24 Hours' },
    { key: 3, value: 'Last 3 Days' },
    { key: 7, value: 'Last 7 Days' },
  ];
  filterErrorMessage: string = '';

  selectedEventData: any;

  scrollPosition: any;

  //pagination components
  previous: number = 0;
  pageSize: number = 10;
  pageNum: number = 1;
  totalPages: number = 0;
  totalRows: number = 0;
  otherParams: any = {
    apiFiltering: true,
    tableRowStyle: (rowData: any) => {
      if (rowData?.is_deleted) {
        return {
          opacity: '0.6',
        };
      }
      return {};
    },
  };
  sortKeys: any = ['updated_at'];

  patrolReportDataListPaginatedDict: any = {};
  loadingData = false;

  patrolReportLogDetailsMobileView: any = {};
  patrolReportLogDetailsTableView: any = [];
  tableStyle = {
    'overflow': 'auto',
    'max-height': '100%',
  };

  userEventLogSubject = new Subject<void>();

  previewPatrolReport = false;
  allowPDFExport = true;

  selectedReportRecipientsListData: any = [];
  reportRecipientListData: any = [];

  checkpointStats = {
    hasCheckpointScans: false,
    hasQrNFCCheckpointScans: false,
  };

  showDataObject = {
    showSummaryOnly: false,
    showCheckpointScanLocation: false,
  };

  previewReportData: any = {};

  previewReportDataAdditionalInfo: any = { hide_guard_column: true };

  previewReportSummaryData: any = [];

  promptFileDataUrls: any = [];
  promptFileIdDataDict: any = {};

  timeZoneOffset: any;

  constructor(
    private clientService: ClientsService,
    private dialogService: ModalDialogueService,
    private viewContainerRef: ViewContainerRef,
    private toasterService: ToasterService,
    private spinnerService: LoadingSpinnerService,
    private router: Router,
    private userService: UsersService,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private jobService: JobsService,
    private checkpointService: CheckpointService,
    private pageService: PagesService,
  ) {}

  ngOnDestroy(): void {}
  ngAfterViewInit(): void {
    const hasRouteInfo = this.formatRouteUrl();
    if (hasRouteInfo) {
      this.selectedEventData = null;
      this.getTableCardDetails();
      this.getPatrolReportLogEvents();
    } else {
      this.openFilterDialog();
    }
  }

  async openFilterDialog() {
    if (!this.initialDataList.client?.length) this.onClientSearch();
    // if (!this.initialDataList.user?.length) this.onUserSearch('');
    if (!this.initialDataList.site?.length) this.onSiteSearch();

    const dialogRef = this.dialogService.open(
      this.patrolFilterTemplate,
      {},
      this.viewContainerRef,
    );
    const dResponse = await firstValueFrom(dialogRef.afterClosed());
    if (!dResponse) {
      this.closeTab.emit();
      this.router.navigate([], {
        queryParams: {},
        replaceUrl: true, // Replaces the current URL without adding a new history entry
      });
      return;
    }
    this.selectedEventData = null;
    this.clearPagination();
    this.getTableCardDetails();
    this.getPatrolReportLogEvents();
  }

  getApiRequestBody() {
    let search_filters: any = {
      event_type_ids: [...this.selectedReportUpdatesToFetch],
    };
    if (this.reportSiteClientLookup.client.data?.id) {
      search_filters['company_id'] =
        this.reportSiteClientLookup.client.data?.id;
    } else {
      search_filters['site_id'] = this.reportSiteClientLookup.site.data?.id;
    }
    if (this.selectedUserData?.id)
      search_filters['sub_user_id'] = this.selectedUserData?.id;
    let body: any = {
      search_filters: search_filters,
      date_range: {
        start: new Date(this.selectedDateRange.start),
        end: new Date(this.selectedDateRange.end),
      },
    };

    if (this.previewPatrolReport) body.add_aws_file_paths = true;

    if (this.sortKeys) body.sort_key = this.sortKeys;

    if (this.pageSize) body.rows = this.pageSize;
    if (this.previous) body.previous = this.previous;

    return body;
  }
  getTotalEventCount(body: any, retries = 1) {
    this.clientService
      .getPatrolReportLog(body, { get_count: 1 })
      .subscribe((response: any) => {
        if (response['status'] == 'success') {
          this.checkpointStats.hasCheckpointScans =
            response?.has_checkpoint_scans;
          this.checkpointStats.hasQrNFCCheckpointScans =
            response?.has_qr_nfc_scans;
          this.totalRows = response?.total_size;
          this.totalPages = Math.ceil(this.totalRows / this.pageSize);

          this.otherParams = {
            ...this.otherParams,
            apiFiltering: true,
            apiCalled: true,
            paginationData: {
              totalRows: this.totalRows,
              previousRows: this.previous,
              rows: this.pageSize,
              pageNum: this.pageNum,
              totalPages: this.totalPages,
              fixedRows: true,
            },
          };

          this.checkAndLoadNextBatch();
        } else {
          if (retries < 3) {
            retries += 1;
            this.getTotalEventCount(body, retries);
          }
        }
      });
  }
  getPatrolReportLogEvents(getCount = true) {
    this.spinnerService.show('Fetching Data');
    const body = this.getApiRequestBody();
    if (getCount) this.getTotalEventCount(body);

    this.clientService.getPatrolReportLog(body).subscribe((response: any) => {
      if (response['status'] == 'success') {
        this.patrolReportDataListPaginatedDict[this.pageNum] =
          response?.data || [];
        this.updateRoutPath();
        this.filterSummaryData();

        this.checkAndLoadNextBatch();
        this.spinnerService.hide();
      } else {
        this.toasterService.setMessage({
          successMessage: '',
          errorMessage: response['message'],
        });
        this.spinnerService.hide();
      }
    });
  }

  checkAndLoadNextBatch() {
    if (
      this.pageNum < this.totalPages &&
      !this.patrolReportDataListPaginatedDict[this.pageNum + 1] &&
      !this.loadingData
    ) {
      this.loadingData = true;
      let body = this.getApiRequestBody();
      body.previous = (body?.previous || 0) + this.pageSize;
      // implies there is one more page to load
      this.clientService
        .getPatrolReportLog(body)
        .pipe(takeUntil(this.userEventLogSubject))
        .subscribe((response: any) => {
          if (response['status'] == 'success') {
            this.patrolReportDataListPaginatedDict[this.pageNum + 1] =
              response?.data || [];
            this.loadingData = false;
          }
        });
    }
  }
  formatRouteUrl() {
    if (!this.route.snapshot.fragment) return false;
    try {
      const urlFragmentDetails = JSON.parse(atob(this.route.snapshot.fragment));

      if (
        urlFragmentDetails &&
        Object.keys(urlFragmentDetails)?.length &&
        urlFragmentDetails?.pageSource == 'patrol-reports'
      ) {
        this.selectedReportUpdatesToFetch = new Set(
          urlFragmentDetails?.selectedReportUpdatesToFetch || [1, 2],
        );

        this.reportSiteClientLookup =
          urlFragmentDetails?.reportSiteClientLookup || {
            client: { data: null as any, disabled: false },
            site: { data: null as any, disabled: false },
          };

        this.selectedUserData = urlFragmentDetails?.selectedUserData || null;

        this.selectedDateRange = urlFragmentDetails?.selectedDateRange || {
          start: getFormattedDateTime(this.initialTimeRange.dateRange.start),
          end: getFormattedDateTime(this.initialTimeRange.dateRange.end),
        };

        if (urlFragmentDetails?.otherParams?.paginationData) {
          this.previous =
            urlFragmentDetails?.otherParams?.paginationData?.previousRows ||
            this.previous;
          this.pageNum =
            urlFragmentDetails?.otherParams?.paginationData?.pageNum ||
            this.pageNum;
          this.pageSize =
            urlFragmentDetails?.otherParams?.paginationData?.rows ||
            this.pageSize;
        }
        return true;
      }
    } catch (error) {}
    return false;
  }
  updateRoutPath() {
    this.router.navigate([], {
      fragment: btoa(
        JSON.stringify({
          otherParams: this.otherParams,
          selectedReportUpdatesToFetch: [...this.selectedReportUpdatesToFetch],
          reportSiteClientLookup: this.reportSiteClientLookup,
          selectedUserData: this.selectedUserData,
          selectedDateRange: this.selectedDateRange,

          pageSource: 'patrol-reports',
        }),
      ),
      queryParams: {},
      replaceUrl: true, // Replaces the current URL without adding a new history entry
    });
  }
  clearPagination() {
    this.patrolReportDataListPaginatedDict = {};
    this.previous = 0;
    this.pageSize = 10;
    this.pageNum = 1;
    this.totalPages = 0;
    this.totalRows = 0;
  }
  onPaginationChange(data: any) {
    this.previous = data?.paginationData?.previousRows || data?.previous;
    this.pageNum = data?.paginationData?.pageNum || data?.pageNum;

    const pageSize = data?.paginationData?.rows || data?.pageSize;

    if (pageSize == this.pageSize) {
      this.pageSize = pageSize;
      // do preloading only here
      if (this.patrolReportDataListPaginatedDict?.[this.pageNum]) {
        this.otherParams.paginationData = {
          totalRows: this.totalRows,
          previousRows: this.previous,
          rows: this.pageSize,
          pageNum: this.pageNum,
          totalPages: Math.ceil(this.totalRows / this.pageSize),
          fixedRows: true,
        };
        this.filterSummaryData();
        this.updateRoutPath();

        this.totalPages = Math.ceil(this.totalRows / this.pageSize);
        const nextPage = this.pageNum + 1;
        if (!this.patrolReportDataListPaginatedDict?.[nextPage])
          this.checkAndLoadNextBatch();
      } else {
        this.loadingData = false;
        this.userEventLogSubject?.next();
        this.getPatrolReportLogEvents(false);
        this.otherParams.paginationData = {
          ...data?.paginationData,
          fixedRows: true,
        };
      }
    } else {
      this.patrolReportDataListPaginatedDict = {};
      this.loadingData = false;
      this.userEventLogSubject?.next();
      this.pageSize = data?.paginationData?.rows;
      this.getPatrolReportLogEvents(false);
      this.otherParams.paginationData = {
        ...data?.paginationData,
        fixedRows: true,
      };
    }
  }
  onUserSearch(event?: any) {
    const searchString = event ? event.target.value : '';
    this.userService
      .getUserListData({ search_str: searchString })
      .subscribe((response: any) => {
        if (response['status'] == 'success') {
          this.userDropDownList = response?.data;
          if (!searchString) {
            this.initialDataList.user = JSON.parse(
              JSON.stringify(this.userDropDownList),
            );
          }
        }
      });
  }
  onUserSelect(data: any) {
    this.selectedUserData = data;
  }
  onClientSearch(event?: any) {
    const searchString = event ? event.target.value : '';
    this.clientService.searchClients(searchString).subscribe((res: any) => {
      if (res['status'] == 'success') {
        this.clientDropDownList = res?.data;
        if (!searchString) {
          this.initialDataList.client = JSON.parse(
            JSON.stringify(this.clientDropDownList),
          );
        }
      }
    });
  }
  onClientSelect(data: any) {
    this.reportSiteClientLookup = {
      client: { data: data, disabled: false },
      site: { data: null, disabled: false },
    };
  }
  onSiteSearch(event?: any) {
    const searchString = event ? event.target.value : '';

    this.clientService
      .searchSites({
        search_str: searchString,
      })
      .subscribe((res: any) => {
        if (res['status'] == 'success') {
          this.siteDropDownList = res?.data?.splice(0, 10);
          if (!event) {
            this.initialDataList.site = JSON.parse(
              JSON.stringify(this.siteDropDownList),
            );
          }
        }
      });
  }
  onSelectSite(data: any) {
    this.reportSiteClientLookup = {
      client: { data: null, disabled: false },
      site: { data: data, disabled: false },
    };
  }

  editField(field: 'site' | 'client' | 'user') {
    if (field == 'user') {
      this.selectedUserData = null;
      this.shiftFocus('userLookupFilterInputId');
    } else {
      this.reportSiteClientLookup[field] = {
        disabled: false,
        data: null,
      };
      if (field == 'site') {
        this.shiftFocus('siteLookupFilterInputId');
      }
      if (field == 'client') {
        this.shiftFocus('clientLookupFilterInputId');
      }
    }

    this.updateInitialDataList(field);
  }
  shiftFocus(elementId: string) {
    setTimeout(() => {
      var element = <HTMLInputElement>document.getElementById(elementId);
      element?.focus();
    }, 200);
  }
  inputFocused(field: 'site' | 'client') {
    setTimeout(() => {
      this.reportSiteClientLookup[field] = {
        data: null,
        disabled: true,
      };
    }, 10);

    this.updateInitialDataList(field);
  }
  updateInitialDataList(field: 'site' | 'client' | 'user') {
    this.siteDropDownList = [];
    this.clientDropDownList = [];
    this.userDropDownList = [];
    setTimeout(() => {
      if (field === 'site') {
        this.siteDropDownList = JSON.parse(
          JSON.stringify(this.initialDataList?.[field] || []),
        );
      } else if (field === 'client') {
        this.clientDropDownList = JSON.parse(
          JSON.stringify(this.initialDataList?.[field] || []),
        );
      } else if (field === 'user') {
        this.userDropDownList = JSON.parse(
          JSON.stringify(this.initialDataList?.[field] || []),
        );
      }
    }, 200);
  }
  setTime(date: Date, time: string) {
    const [hours, minutes] = time?.split(':');
    date.setHours(parseInt(hours), parseInt(minutes), 0, 0);
    return date;
  }
  updateTime(timeObj: any) {
    //https://app.shortcut.com/sendem/story/9830/add-time-of-day-presets-to-datetime-picker

    const startDateTime = this.setTime(
      new Date(this.selectedDateRange.start),
      timeObj?.value,
    );
    this.selectedDateRange.start = getFormattedDateTime(startDateTime);

    const endDateTime = this.setTime(
      new Date(this.selectedDateRange.end),
      timeObj?.value,
    );
    this.selectedDateRange.end = getFormattedDateTime(endDateTime);
    this.onDateTimeCheck();
  }
  updateDate(dateKey: number) {
    const currentEndDateTime = this.selectedDateRange?.end
      ? new Date(this.selectedDateRange.end)
      : new Date();
    const newEndDateTime = new Date();
    newEndDateTime.setHours(
      currentEndDateTime.getHours(),
      currentEndDateTime.getMinutes(),
      0,
      0,
    );
    this.selectedDateRange.end = getFormattedDateTime(newEndDateTime);

    const startDateTime = new Date(newEndDateTime);
    if (dateKey == 0) {
      startDateTime.setHours(startDateTime.getHours() - 12);
    } else {
      startDateTime.setDate(startDateTime.getDate() - dateKey);
    }

    this.selectedDateRange.start = getFormattedDateTime(startDateTime);
    this.onDateTimeCheck();
  }
  onDateTimeCheck() {
    const MAX_LIMIT = 7;
    this.filterErrorMessage = '';
    const start = new Date(this.selectedDateRange?.start).getTime();
    const end = new Date(this.selectedDateRange?.end).getTime();

    if (start > end) {
      this.filterErrorMessage = 'Start Time must be less than end time';
      return;
    }
    if (
      end - start > 1000 * 60 * 60 * 24 * MAX_LIMIT &&
      this.selectedReportUpdatesToFetch.has(2)
    ) {
      this.filterErrorMessage = `Cannot fetch data if time range is greater than ${MAX_LIMIT} days`;
      return;
    }
  }
  getTableCardDetails() {
    let individualDetails = [
      {
        showColumn: !this.selectedUserData?.id,
        name: 'user_name',
        header: 'Guard',
        cardRowStyle: {
          'float': 'right',
          'font-size': 'small',
        },
      },
      {
        showColumn: true,
        name: 'updated_at',
        datetimeObj: true,
        sortKey: 'updated_at',
        cardRowStyle: {
          'font-size': 'small',
        },
      },

      {
        showColumn: true,
        name: 'action',
        suffixNestedValue: (row: any) => {
          return this.selectedReportUpdatesToFetch.size > 1
            ? `[${row?.event_type}]`
            : null;
        },
        suffixStyle: { 'font-size': 'small', 'font-weight': 'bold' },
      },
      {
        showColumn: true,
        nestedValue: (row: any) => {
          return row?.detail?.length > 150
            ? `${row?.detail?.slice(0, 150)}...`
            : row?.detail;
        },
        cardRowStyle: {
          'font-size': 'small',
          'white-space': 'pre-line',
          'color': 'var(--bs-secondary)',
          'margin': '0.5rem 0',
          'display': 'flex',
        },
      },
      {
        showColumn: true,
        name: 'event_key',
        cardRowStyle: { 'font-size': 'small' },
      },

      {
        showColumn: this.reportSiteClientLookup.client.data?.id,
        name: 'site_name',
        cardRowStyle: {
          'font-weight': 'bold',
        },
      },
    ];

    let userEventDetailsTableView = [
      {
        showColumn: true,
        header: 'Event Time',
        name: 'updated_at',
        datetimeObj: true,
        sortKey: 'updated_at',
      },
      // {
      //   showColumn: this.selectedReportUpdatesToFetch.size > 1,
      //   header: 'Event Type',
      //   name: 'event_type',
      // },
      {
        showColumn: true,
        header: 'Event Action',
        name: 'action',
        suffixNestedValue: (row: any) => {
          return this.selectedReportUpdatesToFetch.size > 1
            ? `\n[${row?.event_type}]`
            : null;
        },
        suffixStyle: {
          'font-size': 'small',
          'font-weight': 'bold',
          'white-space': 'pre-line',
        },
      },
      {
        showColumn: true,
        header: this.selectedReportUpdatesToFetch.has(1)
          ? 'Update / Checkpoint'
          : 'Update',
        nestedValue: (row: any) => {
          return row?.detail?.length > 150
            ? `${row?.detail?.slice(0, 150)}...`
            : row?.detail;
        },
      },
      {
        showColumn: this.selectedReportUpdatesToFetch.has(1),
        header: 'Location',
        nestedValue: (row: any) => {
          return row?.gps?.lat && row?.gps?.lon
            ? `${row?.gps?.lat?.toFixed(4)}, ${row?.gps?.lon?.toFixed(4)}`
            : '';
        },
      },
      {
        showColumn: this.reportSiteClientLookup.client.data?.id,
        header: 'Site',
        name: 'site_name',
        columnDataStyle: {
          'white-space': 'pre-line',
        },

        suffixNestedValue: (row: any) => {
          return row?.event_key ? `\n [${row?.event_key}]` : '';
        },
        suffixStyle: {
          'white-space': 'pre-line',
          'font-size': 'small',
        },
      },

      // {
      //   showColumn: !this.selectedUserData?.id,
      //   header: 'User',
      //   name: 'user_name',
      // },
    ];

    this.patrolReportLogDetailsMobileView = {
      individualDetails: [
        ...individualDetails?.filter((item: any) => item?.showColumn),
      ],
      groupDetails: (row: any) => {
        if (row?.is_deleted) {
          return {
            opacity: '0.6',
          };
        }

        return {};
      },
    };

    this.patrolReportLogDetailsTableView = [
      ...userEventDetailsTableView?.filter((item: any) => item?.showColumn),
    ];
  }
  hideDetails() {
    this.selectedEventData = null;

    // let scrollPosition: any = window.localStorage.getItem('scrollToView');
    setTimeout(() => {
      window.scrollTo(0, this.scrollPosition);
    }, 100);
  }
  sortColumn(body: any) {
    this.clearPagination();
    this.sortKeys = body?.sortList || [];
    this.getPatrolReportLogEvents();
  }
  onTableRowClick(event: any) {
    if (event?.id == this.selectedEventData?.id) return;
    this.scrollPosition = window.scrollY;
    this.selectedEventData = { ...event };
    this.tableStyle = {
      'overflow': 'auto',
      'max-height': '35vh',
    };
    if (event?.id && event?.event_type) {
      // call backend to get detailed info if its event log
      let body: any = {
        event_type: 'user_update_history_detail',
        update_type: event?.event_type,
        event_id: btoa(event?.id),
      };
      this.userService.getUserUpdateLog(body).subscribe((response: any) => {
        if (response['status'] == 'success') {
          this.selectedEventData = { ...event, ...response?.data };
        } else {
          this.toasterService.setMessage({
            successMessage: '',
            errorMessage: response['message'],
          });
        }
      });
    }
  }
  openTemplate(
    template?: any,
    source: 'pdfRecipients' | 'xlRecipients' = 'pdfRecipients',
  ) {
    this.getReportRecipients();

    this.dialogService.open(
      template,
      { data: { source } },
      this.viewContainerRef,
    );
  }
  getReportRecipients() {
    let params: any = { get_manual_patrol_report_contacts: 1 };

    if (this.reportSiteClientLookup?.site?.data?.id) {
      params.site_id = this.reportSiteClientLookup?.site?.data?.id;
    }
    if (this.reportSiteClientLookup?.client?.data?.id) {
      params.company_id = this.reportSiteClientLookup?.client?.data?.id;
    }

    this.clientService
      .searchLocationContacts(params)
      .subscribe((response: any) => {
        if (response['status'] == 'success') {
          this.reportRecipientListData = response?.data;

          this.selectedReportRecipientsListData = [
            ...new Set(
              this.reportRecipientListData
                ?.filter((recipient: any) => !recipient?.isSnubbed)
                ?.map((recipient: any) => recipient?.pk),
            ),
          ];
        } else {
          this.toasterService.setMessage({
            successMessage: '',
            errorMessage: response['message'],
          });
        }
      });
  }
  selectAllContacts(event: any) {
    const checked = event.target.checked;
    if (checked) {
      this.reportRecipientListData?.forEach(
        (contact: any) => (contact.isSnubbed = false),
      );
      this.selectedReportRecipientsListData = [
        ...new Set(
          this.reportRecipientListData
            ?.filter((recipient: any) => !recipient?.isSnubbed)
            ?.map((recipient: any) => recipient?.pk),
        ),
      ];
    } else {
      this.selectedReportRecipientsListData = [];
      this.reportRecipientListData?.forEach(
        (contact: any) => (contact.isSnubbed = true),
      );
    }
  }

  selectUnselectRecipients(contact: any) {
    contact.isSnubbed = !contact?.isSnubbed;

    this.selectedReportRecipientsListData = [
      ...new Set(
        this.reportRecipientListData
          ?.filter((recipient: any) => !recipient?.isSnubbed)
          ?.map((recipient: any) => recipient?.pk),
      ),
    ];
  }
  exportData(exportType: 'pdf' | 'xlsx') {
    let body: any = {
      ...this.getApiRequestBody(),

      additional_info: {
        ...this.previewReportDataAdditionalInfo,
        summary_only: this.showDataObject.showSummaryOnly,
        show_checkpoint_scan_location:
          this.showDataObject.showCheckpointScanLocation,
        user_ids: this.selectedReportRecipientsListData,
      },
    };

    let params = {
      [exportType == 'pdf' ? 'export_pdf' : 'export_checkpoint_scan_xlsx']: 1,
    };

    this.spinnerService.show('Generating Report');
    this.clientService
      .exportPatrolReportLog(body, params)
      .then((response: any) => {
        if (response['status'] == 'success') {
          this.toasterService.setMessage({
            successMessage: response['message'],
            errorMessage: '',
          });

          // this.restartEverything();
          this.spinnerService.hide();
        } else {
          this.toasterService.setMessage({
            successMessage: '',
            errorMessage: response['message'],
          });
          this.spinnerService.hide();
        }
      });
  }
  navigateToSiteClient(route: string, id: any, fragment: string = 'pc') {
    if (route === 'site') {
      this.router.navigate(['/view-site', { sKey: String(id) }], {
        fragment: fragment,
      });
    } else {
      this.router.navigate(['/view-client', { cKey: String(id) }], {
        fragment: fragment,
      });
    }
  }

  getSiteClientReportInfo() {
    let body: any = { event_type: 'preview_patrol_report' };

    if (this.reportSiteClientLookup?.site?.data?.id) {
      body.site_id = this.reportSiteClientLookup?.site?.data?.id;
    } else {
      body.company_id = this.reportSiteClientLookup?.client?.data?.id;
    }

    this.userService.getUserUpdateLog(body).subscribe((response: any) => {
      this.previewReportData = response?.data || {};
      this.timeZoneOffset = convertUTCStringToOffset(
        this.previewReportData?.patrol_detail?.time_zone_diff,
      );
      this.spinnerService.hide();
    });
  }
  async modifyLetterHeadLogo() {
    const dialogRef = this.dialogService.open(ModifyLetterheadLogoComponent, {
      data: {
        logoUsed:
          this.previewReportData?.patrol_detail?.subscriber_info?.logo_used,
        siteData: {
          id: this.reportSiteClientLookup?.site?.data?.id,
          company_name: this.reportSiteClientLookup?.site?.data?.company_name,
        },
        clientData: {
          id:
            this.reportSiteClientLookup?.site?.data?.company?.id ||
            this.reportSiteClientLookup?.client?.data?.id,

          company_name:
            this.reportSiteClientLookup?.site?.data?.company?.company_name ||
            this.reportSiteClientLookup?.client?.data?.company_name,
          is_subcontracting:
            this.previewReportData?.patrol_detail?.is_subcontracting,
        },
      },
    });
    const dRes = await firstValueFrom(dialogRef.afterClosed());

    if (dRes) this.getSiteClientReportInfo();
  }
  filterSummaryData() {
    const events = JSON.parse(
      JSON.stringify(this.patrolReportDataListPaginatedDict?.[this.pageNum]),
    );
    if (this.previewReportDataAdditionalInfo?.hide_uploads) {
      this.previewReportSummaryData = events?.filter(
        (update: any) =>
          !update?.file_url ||
          (update?.event_action == 0 && update?.event_type == 'Patrol Update'),
      );
    } else {
      this.previewReportSummaryData = events;
    }
    this.getPromptFileIds();
  }

  async editEventTimeStamp(rowData: any, template: any) {
    const currentValue = getFormattedDateTime(rowData?.updated_at);
    let maxDateTime = new Date();
    let minDateTime: any;

    const dialogRef = this.dialogService.open(
      template,
      {
        data: {
          name: rowData?.action,
          currentValue: currentValue,
          newValue: currentValue,
          minDateTime: minDateTime ? getFormattedDateTime(minDateTime) : null,
          maxDateTime: maxDateTime ? getFormattedDateTime(maxDateTime) : null,
        },
      },
      this.viewContainerRef,
    );

    const dRes = await firstValueFrom(dialogRef.afterClosed());
    if (!dRes) return;

    if (rowData?.event_type == 'Job Update') {
      let body: any = {
        event_id: rowData?.id,
        user_provided_timestamp: new Date(dRes?.newValue || dRes?.currentValue),
        change_event_timestamp: 1,
      };
      this.updateData(body, rowData?.event_id, true);
    } else {
      let body: any = {
        event_id: rowData?.id,
        user_provided_timestamp: new Date(dRes?.newValue || dRes?.currentValue),
        change_event_timestamp: 1,
      };

      this.updateUserUpdate(body);
    }
  }
  updateData(body: any, jobId: string, updateApi = false) {
    let requestData: any = {};
    let apiRoute: any;
    if (updateApi) {
      requestData['method'] = 'put';
      requestData['url'] = 'api/job/' + jobId + '/';
      requestData['id'] = jobId;
      requestData['data'] = body;
      apiRoute = this.jobService.updateJob(requestData, {
        return_empty_response: 1,
      });
    } else {
      requestData = { ...body };
      apiRoute = this.jobService.jobUpdateWoSW(requestData, {
        return_empty_response: 1,
      });
    }

    this.spinnerService.show('Updating Info');
    apiRoute.then((response: any) => {
      if (response?.status == 'success') {
        setTimeout(() => {
          this.spinnerService.hide();
          this.getPatrolReportLogEvents();
          this.toasterService.setMessage({
            successMessage: response?.message,
            errorMessage: '',
          });
        }, 100);
      } else {
        this.spinnerService.hide();
        this.toasterService.setMessage({
          successMessage: '',
          errorMessage: response?.message,
        });
      }
    });
  }
  updateUserUpdate(body: any) {
    this.spinnerService.show('Updating Info');
    this.checkpointService.updateClient(body).then((response: any) => {
      if (response?.status == 'success') {
        setTimeout(() => {
          this.spinnerService.hide();
          this.getPatrolReportLogEvents();
          this.toasterService.setMessage({
            successMessage: response?.message,
            errorMessage: '',
          });
        }, 100);
      } else {
        this.spinnerService.hide();
        this.toasterService.setMessage({
          successMessage: '',
          errorMessage: response?.message,
        });
      }
    });
  }

  changeStatusTimeStamp(event: any, eventData: any) {
    const selectedTime = event?.target.value
      ? new Date(event?.target.value).getTime()
      : new Date(eventData?.currentValue).getTime();

    let valid = true;

    if (eventData?.minDateTime)
      valid = selectedTime >= new Date(eventData?.minDateTime)?.getTime();

    if (eventData?.maxDateTime)
      valid =
        valid && selectedTime <= new Date(eventData?.maxDateTime)?.getTime();

    eventData.valid = valid;
  }
  scrollToSection(event: Event, sectionId: string) {
    event.preventDefault();
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
  }
  getFileIdsFromNestedData(innerItem: any, newPromptFileIdDataDict: any) {
    if (innerItem?.options && innerItem?.options?.length) {
      innerItem?.options?.forEach((item: any) => {
        if (item?.file_id) {
          newPromptFileIdDataDict[item?.file_id] = item?.description;
        }
        this.getFileIdsFromNestedData(item, newPromptFileIdDataDict);
      });
    }
  }
  getPromptFileIds() {
    const newPromptFileIdDataDict: any = {};
    this.previewReportSummaryData?.forEach((item: any) => {
      if (item?.prompts?.length) {
        item?.prompts?.forEach((outerItem: any) => {
          outerItem?.data?.forEach((innerItem: any) => {
            if (innerItem?.file_id) {
              newPromptFileIdDataDict[innerItem?.file_id] =
                innerItem?.description;
            }
            this.getFileIdsFromNestedData(innerItem, newPromptFileIdDataDict);
          });
        });
      }
    });

    this.promptFileIdDataDict =
      JSON.parse(JSON.stringify(newPromptFileIdDataDict)) || {};

    const promptFileIds = Object.keys(this.promptFileIdDataDict) || [];

    if (promptFileIds?.length) {
      this.getS3Urls(promptFileIds);
    }
  }
  getS3Urls(promptFileIds: any) {
    this.pageService
      .manageS3Urls({ file_ids: promptFileIds }, { get_file_urls: 1 })
      .subscribe((response: any) => {
        this.promptFileDataUrls = [];
        const responseData = response?.data || {};
        for (const file_id in responseData) {
          let resp: any = responseData?.[file_id] || {};
          resp['file_id'] = file_id;
          resp['description'] = this.promptFileIdDataDict?.[file_id] || '';
          this.promptFileDataUrls.push(resp);
        }

        this.cdr.detectChanges();
      });
  }
  deleteComment(rowData: any) {
    this.spinnerService.show();

    if (rowData?.event_type == 'Job Update') {
      let body: any = {
        job_id: rowData?.event_id,
        comment: '',
        id: rowData?.id,
        edit_comment: 1,
      };

      this.updateData(body, rowData?.event_id);
    } else {
      let body = {
        company_id: rowData?.company_id,
        comment: '',
        id: rowData?.id,
        edit_comment: 1,
      };
      this.updateUserUpdate(body);
    }
  }
  async openCommentTemplate(commentTemplate: any, rowData: any) {
    const data = {
      newComment: rowData?.detail,
      previousValue: rowData?.detail,
      ...rowData,
    };
    const dialogRef = this.dialogService.open(
      commentTemplate,
      {
        data: data,
      },
      this.viewContainerRef,
    );
    const dResponse = await firstValueFrom(dialogRef.afterClosed());
    if (!dResponse) return;
    if (!data?.file_id && !data?.newComment?.trim()) {
      this.toasterService.setMessage({
        successMessage: '',
        errorMessage: 'Comment cannot be empty',
      });
      return;
    }
    if (rowData?.event_type == 'Job Update') {
      let body: any = {
        job_id: rowData?.event_id,
        comment: data?.newComment?.trim(),
        id: rowData?.id,
        edit_comment: 1,
      };

      this.updateData(body, rowData?.event_id);
    } else {
      let body = {
        company_id: rowData?.company_id,
        comment: data?.newComment?.trim(),
        id: rowData?.id,
        edit_comment: 1,
      };
      this.updateUserUpdate(body);
    }
  }

  async deleteRestoreUserUpdate(event: any) {
    const dialogRef = this.dialogService.open(ConfirmDialogComponent, {
      data: {
        title: event?.type === 'delete' ? 'Delete Event' : 'Restore Event',
        message:
          event?.type === 'delete'
            ? `Are you sure you want to delete this event?`
            : 'Are you sure you want to restore this event?',
      },
    });

    const dRes = await firstValueFrom(dialogRef.afterClosed());

    if (dRes === true) {
      const rowData = event?.event;
      this.spinnerService.show(
        event?.type === 'delete' ? 'Removing Update' : 'Restoring Update',
      );
      if (rowData?.event_type == 'Job Update') {
        let body: any = {
          id: rowData?.id,
          job_id: rowData?.event_id,
          [event?.type === 'delete' ? 'delete_update' : 'restore_update']: 1,
        };

        this.updateData(body, rowData?.event_id);
      } else {
        let body: any = {
          id: rowData?.id,
          [event?.type === 'delete' ? 'delete_update' : 'restore_update']: 1,
        };
        this.updateUserUpdate(body);
      }
    }
  }
  clearAndFocusTextBox(elementId: string) {
    setTimeout(() => {
      var element = <HTMLInputElement>document.getElementById(elementId);
      element?.focus();
    }, 100);
  }
  onOpenImage(event: any, data: any) {
    var target = event.target || event.srcElement || event.currentTarget;
    var srcAttr = target.attributes.src;

    this.dialogService.open(MediaPreviewComponent, {
      data: {
        mediaSrc: srcAttr.nodeValue,
        timeStamp: data?.updated_at,
      },
    });
  }
  getPatrolPreviewDetails() {
    this.spinnerService.show('Fetcing PDF preview data');
    this.previewPatrolReport = !this.previewPatrolReport;
    this.selectedEventData = null;
    this.clearPagination();
    this.getPatrolReportLogEvents();
    this.getSiteClientReportInfo();
  }
}
